import { createSelector, createSlice } from '@reduxjs/toolkit';
import * as R from 'ramda';
import { CancelToken } from 'axios';

// Utils
import http from 'utils/http';
import { setFilters } from 'feature/loading-management/filters/store/filtersSlice';
import { getStringifiedQueryParams } from 'utils/helpers/fetch';
import { EXTENDED_LOADING_FILTER, SELF_AUTH_FILTERS } from 'utils/constants';
import { getActivationType } from 'feature/loading-management/utils/helpers';
import { createSnack } from 'snackbar/snackbarSlice';
import { sanitizeSelfAuthQueryParams } from '../self-authorizations-grid/selfAuthListViewUtils';

const initialState = {
  activeSelfAuthTab: 'baseProducts',
  authorizationError: '',
  dataList: [],
  dataListPending: true,
  dialogSelfAuthorization: {},
  error: '',
  mobileDrawerOpen: false,
  mobileFilterDrawerOpen: false,
  productActivationError: '',
  selfAuthorizationDialogOpen: null,
  selfAuthorizations: {},
  selfAuthorizationsPending: true,
  toggleExtendedLoadingError: '',
  toggleExtendedLoadingSuccess: '',
};

const reducers = {
  resetSelfAuth() {
    return initialState;
  },
  setActiveSelfAuthTab(state, { payload }) {
    return R.assoc('activeSelfAuthTab', payload, state);
  },
  setAuthorizationSetupError(state, { payload }) {
    return R.assoc('authorizationError', payload.error, state);
  },
  setDataList(state, { payload }) {
    return R.assoc('dataList', payload, state);
  },
  setDataListPending(state, { payload }) {
    return R.assoc('dataListPending', payload, state);
  },
  setDialogSelfAuthorization(state, { payload }) {
    return R.assoc('dialogSelfAuthorization', payload, state);
  },
  setExtendedLoadingError(state, { payload }) {
    // errorOrMessage :: Payload a => a -> String
    const errorOrMessage = R.either(R.prop('error'), R.prop('message'));
    return R.assoc('toggleExtendedLoadingError', errorOrMessage(payload), state);
  },
  setExtendedLoadingSelection(state, { payload }) {
    return R.assoc('extendedLoadingSelection', payload, state);
  },
  setExtendedLoadingSuccess(state, { payload }) {
    return R.assoc('toggleExtendedLoadingSuccess', payload.success, state);
  },
  setMobileDrawerOpen(state, { payload }) {
    return R.assoc('mobileDrawerOpen', payload, state);
  },
  setMobileFilterDrawerOpen(state, { payload }) {
    return R.assoc('mobileFilterDrawerOpen', payload, state);
  },
  setProductActivationError(state, { payload }) {
    // errorOrMessage :: Payload a => a -> String
    const errorOrMessage = R.either(R.prop('error'), R.prop('message'));
    return R.assoc('productActivationError', errorOrMessage(payload), state);
  },
  setSelfAuthorizationDialogOpen(state, { payload }) {
    return R.assoc('selfAuthorizationDialogOpen', payload, state);
  },
  setSelfAuthorizations(state, { payload }) {
    return R.assoc('selfAuthorizations', payload, state);
  },
  setSelfAuthorizationsError(state, { payload }) {
    return R.assoc('error', payload.error, state);
  },
  setSelfAuthorizationsPending(state, { payload }) {
    return R.assoc('selfAuthorizationsPending', payload, state);
  },
  updateExtendedLoading(state, { payload }) {
    const {
      data: { productLocationAuthorizationId, extendedLoadingAccepted },
    } = payload;

    // product :: BaseProduct a => a -> Boolean
    // looks in the "activeAuthorization" part of each element to find where productLocationAuthorizationId is a match where it returns true
    const product = R.compose(
      R.propEq('productLocationAuthorizationId', productLocationAuthorizationId),
      R.prop('activeAuthorization')
    );

    // calls the function with the array of the 'baseProducts' in self authorizations, for extended loading it will always be base products
    // returns an array index value when product returns true
    const idx = R.findIndex(product, state.selfAuthorizations.baseProducts);

    // overrides the selfAuthorizations.baseProducts[idx].activeAuthorization.extendedLoadingAccepted node in state with extendedLoadingAccepted
    return R.assocPath(
      ['selfAuthorizations', 'baseProducts', idx, 'activeAuthorization', 'extendedLoadingAccepted'],
      extendedLoadingAccepted,
      state
    );
  },
  // This function is used in the createNewSelfAuth wizard
  // It can also be used to update on toggle, but it was decided that we would fetch new data
  // Keeping this note here in case we ever want to revert to updating locally
  updateSelfAuthorizations(state, { payload }) {
    const {
      listType,
      data: { productId, locationId, ...activeAuthorization },
    } = payload;
    const idx = state.selfAuthorizations[listType].findIndex(
      ({ location, product }) => location.id === locationId && product.id === productId
    );

    // overrides the selfAuthorizations[listType][idx].activeAuthorization node in state with activeAuthorization
    return R.assocPath(
      ['selfAuthorizations', listType, idx, 'activeAuthorization'],
      activeAuthorization,
      state
    );
  },
};

const { actions, reducer } = createSlice({
  initialState,
  name: 'selfAuthorizations',
  reducers,
});

export const {
  resetSelfAuth,
  setActiveSelfAuthTab,
  setAuthorizationSetupError,
  setExtendedLoadingError,
  setExtendedLoadingSelection,
  setExtendedLoadingSuccess,
  setDataList,
  setDataListPending,
  setDialogSelfAuthorization,
  setMobileDrawerOpen,
  setMobileFilterDrawerOpen,
  setProductActivationError,
  setSelfAuthorizationDialogOpen,
  setSelfAuthorizations,
  setSelfAuthorizationsError,
  setSelfAuthorizationsPending,
  updateExtendedLoading,
  updateSelfAuthorizations,
} = actions;

// Selectors
export const getSelfAuthorizationItem = item =>
  R.path(['loadingManagement', 'selfAuthorizations', item]);

export const getSelectorsData = createSelector(
  state => state.loadingManagement.filters.searchTerm,
  state => state.loadingManagement.selfAuthorizations.dataList,
  (searchTerm, dataList) => {
    if (searchTerm) {
      return dataList.filter(({ nameShort }) =>
        nameShort.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return dataList;
  }
);

export const getCurrentSelection = createSelector(
  getSelectorsData,
  state => state.loadingManagement.filters.selectedIndex,
  (selectorsData, selectedIndex) => selectorsData[selectedIndex] || {}
);

const selfAuthFilter = R.pipe(
  R.path(['loadingManagement', 'filters', 'authorizationSelection']),
  R.cond([
    [R.equals(SELF_AUTH_FILTERS.all), () => R.T],
    [R.equals(SELF_AUTH_FILTERS.turnedOn), () => item => !!item.activeAuthorization],
    [R.equals(SELF_AUTH_FILTERS.turnedOff), () => item => !item.activeAuthorization],
  ])
);

const extendedLoadingFilter = R.pipe(
  R.path(['loadingManagement', 'filters', 'extendedLoadingSelection']),
  R.cond([
    [R.equals(EXTENDED_LOADING_FILTER.all), () => R.T],
    [
      R.equals(EXTENDED_LOADING_FILTER.turnedOn),
      () => item =>
        item.activeAuthorization?.extendedLoadingAllowed &&
        item.activeAuthorization?.extendedLoadingAccepted,
    ],
    [
      R.equals(EXTENDED_LOADING_FILTER.turnedOff),
      () => item =>
        item.activeAuthorization?.extendedLoadingAllowed &&
        !item.activeAuthorization?.extendedLoadingAccepted,
    ],
    [
      R.equals(EXTENDED_LOADING_FILTER.notAvailable),
      () => item => !item.activeAuthorization?.extendedLoadingAllowed,
    ],
  ])
);

const combineFilters = createSelector(extendedLoadingFilter, selfAuthFilter, R.both);

const getUnFilteredCards = createSelector(
  R.path(['loadingManagement', 'selfAuthorizations', 'activeSelfAuthTab']),
  getSelfAuthorizationItem('selfAuthorizations'),
  R.propOr([])
);

// getCardsToDisplay :: function, array -> array
export const getCardsToDisplay = createSelector(combineFilters, getUnFilteredCards, R.filter);

// Actions
// none of these actions use async/await syntax, no need to add async in front of dispatch calls
// fetchSelfAuthorizations is called from ProductConfirmationDialog, ConfirmAndSubmit (create new self auth) and Sidebar
// please ensure all are updated if anything here changes
let fetchSelfAuthCancelToken;
export const fetchSelfAuthorizations = queryParams => dispatch => {
  const paramString = getStringifiedQueryParams(sanitizeSelfAuthQueryParams(queryParams));
  dispatch(setSelfAuthorizationsPending(true));

  if (fetchSelfAuthCancelToken !== undefined) {
    fetchSelfAuthCancelToken.cancel(
      'fetchSelfAuthCancelToken operation canceled due to new request.'
    );
  }

  // Save the cancel token for the current request
  fetchSelfAuthCancelToken = CancelToken.source();

  const url = `self-authorizations/view-authorizations?${paramString}`;

  http
    .get(url, { cancelToken: fetchSelfAuthCancelToken.token })
    .then(({ data }) => dispatch(setSelfAuthorizations(data)))
    .catch(err => {
      dispatch(setSelfAuthorizations({}));
      dispatch(
        createSnack({
          message: `Error fetching self authorizations: ${err.response.data.title}`,
          options: {
            variant: 'error',
          },
        })
      );
    })
    .finally(() => dispatch(setSelfAuthorizationsPending(false)));
};

// fetchSelfAuthorizations is called from both ProductConfirmationDialog and Sidebar
// please ensure both are updated if anything here changes
let fetchUniqueSelectorsToken;
export const fetchUniqueSelectors =
  (queryParams, viewBy, resetDataAndPendingState = true) =>
  dispatch => {
    const paramString = getStringifiedQueryParams(sanitizeSelfAuthQueryParams(queryParams));
    dispatch(setSelfAuthorizations({}));
    // this is a check to see if we are toggling a product and do not need to show skeletons
    // please do not remove unless you find a better way to implement
    if (resetDataAndPendingState) {
      dispatch(setFilters({ name: 'selectedIndex', value: undefined }));
      dispatch(setSelfAuthorizationsPending(true));
      dispatch(setDataListPending(true));
      dispatch(setDataList([]));
    }

    if (fetchUniqueSelectorsToken !== undefined) {
      fetchUniqueSelectorsToken.cancel(
        'fetchUniqueSelectorsToken operation canceled due to new request.'
      );
    }

    // Save the cancel token for the current request
    fetchUniqueSelectorsToken = CancelToken.source();

    const URL = `self-authorizations/get-unique-${viewBy}s-list?${paramString}`;
    http
      .get(URL, { cancelToken: fetchUniqueSelectorsToken.token })
      .then(({ data }) => dispatch(setDataList(data)))
      .catch(err =>
        dispatch(
          createSnack({
            message: `Error fetching unique ${viewBy}s: ${err.response.data.title}`,
            options: {
              variant: 'error',
            },
          })
        )
      )
      .finally(() => dispatch(setDataListPending(false)));
  };

let toggleExtendedLoadingToken;
export const toggleExtendedLoading =
  ({ extendedLoadingAccepted, locationName, productLocationAuthorizationId, productName }) =>
  dispatch => {
    dispatch(setExtendedLoadingSuccess({ success: '' }));
    dispatch(setExtendedLoadingError({ error: '' }));

    if (toggleExtendedLoadingToken !== undefined) {
      toggleExtendedLoadingToken.cancel(
        'toggleExtendedLoadingToken operation canceled due to new request.'
      );
    }

    // Save the cancel token for the current request
    toggleExtendedLoadingToken = CancelToken.source();

    return http
      .post(
        `self-authorizations/${
          extendedLoadingAccepted ? 'deactivate' : 'activate'
        }-extended-loading`,
        { productLocationAuthorizationId },
        { cancelToken: toggleExtendedLoadingToken.token }
      )
      .then(({ data }) => {
        dispatch(updateExtendedLoading({ data }));
        dispatch(
          createSnack({
            message: `Extended Loading for ${productName} is now ${
              extendedLoadingAccepted ? 'inactive' : 'active'
            } at ${locationName}`,
          })
        );
      })
      .catch(err =>
        dispatch(
          createSnack({
            message: `Error ${
              extendedLoadingAccepted ? 'deactivating' : 'activating'
            } extended loading: ${err.response.data.title}`,
            options: {
              variant: 'error',
            },
          })
        )
      )
      .finally(() => {
        dispatch(setExtendedLoadingSuccess({ success: '' }));
        dispatch(setSelfAuthorizationDialogOpen(null));
        dispatch(setDialogSelfAuthorization({}));
      });
  };

let toggleProductActivationController;
export const toggleProductActivation =
  ({ active, listType, locationName, productLocationAuthorizationId, productName, ...postBody }) =>
  dispatch => {
    dispatch(setProductActivationError({ error: '' }));

    if (toggleProductActivationController) toggleProductActivationController.abort();

    // A new instance of the AbortController is created before making the API request
    toggleProductActivationController = new AbortController();

    // grab a reference to its associated AbortSignal object using the AbortController.signal property
    const { signal } = toggleProductActivationController;

    dispatch(setSelfAuthorizationsPending(true));

    if (active) {
      return http
        .post(
          `self-authorizations/deactivate-self-authorization`,
          { productLocationAuthorizationId },
          {
            signal,
          }
        )
        .catch(err =>
          dispatch(
            createSnack({
              message: `Error deactivating self authorization: ${err.response.data.title}`,
              options: {
                variant: 'error',
              },
            })
          )
        )
        .finally(() => {
          dispatch(setSelfAuthorizationsPending(false));
          dispatch(
            createSnack({
              message: `${productName} is now deactivated at ${locationName}`,
            })
          );
          dispatch(setSelfAuthorizationDialogOpen(null));
          dispatch(setDialogSelfAuthorization({}));
        });
    }
    const activationType = getActivationType(listType);
    return http
      .post(`self-authorizations/activate-${activationType}-authorization`, postBody, signal)
      .catch(err =>
        dispatch(
          createSnack({
            message: `Error activating self authorization: ${err.response.data.title}`,
            options: {
              variant: 'error',
            },
          })
        )
      )
      .finally(() => {
        dispatch(setSelfAuthorizationsPending(false));
        dispatch(
          createSnack({
            message: `${productName} is now active at ${locationName}.`,
          })
        );
        dispatch(setSelfAuthorizationDialogOpen(null));
        dispatch(setDialogSelfAuthorization({}));
      });
  };

export default reducer;
